import { useState, useCallback, useRef } from 'react';
import { yupToFormErrors } from 'formik';
import type { FormikProps } from 'formik';
import { ValidationError } from 'yup';
import { publishEventSchema } from './form';
import type { EventFormValues } from './form';

/**
 * Manages the publish flow: pre-publish validation against publishEventSchema,
 * dialog state, and status override.
 *
 * Uses a ref for status override so the submit handler can read it synchronously
 * without relying on async Formik state updates.
 */
export function usePublishFlow(formikRef: React.RefObject<FormikProps<EventFormValues> | null>) {
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const statusOverrideRef = useRef<string | null>(null);

  const handleOpenPublishDialog = useCallback(async () => {
    if (!formikRef.current) return;
    try {
      await publishEventSchema.validate(
        { ...formikRef.current.values, status: 'PUBLISHED' },
        { abortEarly: false },
      );
      setPublishDialogOpen(true);
    } catch (error) {
      if (error instanceof ValidationError) {
        const errors = yupToFormErrors<EventFormValues>(error);
        formikRef.current.setErrors(errors);
        const touched = Object.keys(errors).reduce<Record<string, boolean>>((acc, key) => {
          acc[key] = true;
          return acc;
        }, {});
        formikRef.current.setTouched(touched, false);
      } else {
        throw error;
      }
    }
  }, [formikRef]);

  const handlePublishConfirm = useCallback((status: string) => {
    setPublishDialogOpen(false);
    if (formikRef.current) {
      statusOverrideRef.current = status;
      formikRef.current.submitForm();
    }
  }, [formikRef]);

  const handleClosePublishDialog = useCallback(() => {
    setPublishDialogOpen(false);
  }, []);

  /**
   * Call at the start of handleSubmit to get form values with the effective status applied.
   * Consumes and clears the override so it doesn't persist across submissions.
   */
  const consumeStatusOverride = useCallback((formValues: EventFormValues): EventFormValues => {
    if (statusOverrideRef.current) {
      const override = statusOverrideRef.current;
      statusOverrideRef.current = null;
      return { ...formValues, status: override };
    }
    return formValues;
  }, []);

  return {
    publishDialogOpen,
    handleOpenPublishDialog,
    handlePublishConfirm,
    handleClosePublishDialog,
    statusOverrideRef,
    consumeStatusOverride,
  };
}
